import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  TrendingUp, ShoppingCart, Package, Users, DollarSign,
  BarChart2, PieChart, Clock, Mail, RefreshCw, ArrowUp,
  ArrowDown, AlertTriangle, CheckCircle, Send, Globe,
  ChevronRight, Loader2, X, Trash2
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';

// ── Countdown ─────────────────────────────────────────────────────────────────
const Countdown = ({ expiresAt }) => {
  const [rem, setRem] = useState(null);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setRem(Math.max(0, new Date(expiresAt) - new Date()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (rem === null) return null;
  if (rem === 0) return <span className="text-[10px] text-red-500 font-semibold">Süresi doldu</span>;
  const h = Math.floor(rem / 3600000);
  const m = Math.floor((rem % 3600000) / 60000);
  const s = Math.floor((rem % 60000) / 1000);
  const color = h === 0 ? 'text-red-500' : h < 3 ? 'text-amber-500' : 'text-emerald-600';
  return (
    <span className={`text-[11px] font-mono font-semibold ${color}`}>
      {h > 0 ? `${h}s ${m}d ${s}sn` : `${m}d ${s}sn`}
    </span>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color = 'emerald', trend }) => {
  const colors = {
    emerald: 'from-emerald-500 to-teal-600',
    blue:    'from-blue-500 to-indigo-600',
    amber:   'from-amber-500 to-orange-600',
    purple:  'from-purple-500 to-violet-600',
    red:     'from-red-500 to-rose-600',
    gray:    'from-gray-500 to-slate-600',
  };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-extrabold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center shrink-0`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
      {trend != null && (
        <div className={`flex items-center gap-1 mt-3 text-xs font-semibold ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {Math.abs(trend)}% bu hafta
        </div>
      )}
    </div>
  );
};

// ── Bar Chart (CSS) ───────────────────────────────────────────────────────────
const MiniBarChart = ({ data, valueKey = 'revenue', labelKey = 'day' }) => {
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-1.5 h-24">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full rounded-t-md bg-gradient-to-t from-emerald-500 to-emerald-300 transition-all duration-500"
            style={{ height: `${Math.round((d[valueKey] / max) * 80)}px`, minHeight: d[valueKey] > 0 ? '4px' : '0' }}
            title={`${d[labelKey]}: ${d[valueKey]}`}
          />
          <span className="text-[9px] text-gray-400 font-mono">{d[labelKey]}</span>
        </div>
      ))}
    </div>
  );
};

// ── Zone Badge ────────────────────────────────────────────────────────────────
const ZoneBadge = ({ zone }) => {
  const colors = {
    'Blue Zone':  'bg-blue-100 text-blue-700',
    'Green Zone': 'bg-emerald-100 text-emerald-700',
    'Supplier':   'bg-orange-100 text-orange-700',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colors[zone] || 'bg-gray-100 text-gray-500'}`}>{zone || '—'}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
export default function UltraAdminSalesReports() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const urlTab = searchParams.get('tab') || 'dashboard';
  const [tab, setTab] = useState(urlTab);

  const changeTab = (t) => {
    setTab(t);
    setSearchParams({ tab: t });
  };
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [byTenant, setByTenant] = useState([]);
  const [byZone, setByZone] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [sendingTo, setSendingTo] = useState(null);
  const [expandedCart, setExpandedCart] = useState(null);
  const [deleting, setDeleting] = useState(null);   // user_id currently being deleted (whole cart)
  const [deletingItem, setDeletingItem] = useState(null); // `${user_id}:${item_id}` for per-item deletion
  const [deletingOrder, setDeletingOrder] = useState(null); // order_id currently being deleted
  const [confirmDelete, setConfirmDelete] = useState(null); // { scope: 'cart' | 'item' | 'order', cart?, item?, order? }
  const [forceDeleteOrder, setForceDeleteOrder] = useState(null); // { order, payment_status, status } — secondary prompt for paid/confirmed orders
  const [forcePassword, setForcePassword] = useState('');

  // Statuses that the backend allows Ultra Admin to hard-delete
  const UNPAID_ORDER_STATUSES = new Set([
    'pending_confirmation', 'awaiting_confirmation',
    'quote_pending', 'quote_offered', 'quote_rejected', 'quote_expired',
    'cancelled', 'canceled', 'draft', 'rejected', 'pending',
  ]);
  const isOrderDeletable = (o) => UNPAID_ORDER_STATUSES.has((o?.status || '').toLowerCase());

  // Drill-down modal: selected tenant orders
  const [tenantDrill, setTenantDrill] = useState(null);         // { tenant_id, company_name, zone, orders[] }
  const [tenantDrillLoading, setTenantDrillLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null); // which order row is expanded (items visible)

  const openTenantDrill = async (tenantId) => {
    setTenantDrillLoading(true);
    setTenantDrill({ tenant_id: tenantId, loading: true, orders: [] });
    try {
      const r = await apiClient.get(`/ultra-admin/sales/tenant/${tenantId}/orders`);
      setTenantDrill(r.data);
    } catch (e) {
      toast.error(tr ? 'Tenant siparişleri yüklenemedi.' : 'Failed to load tenant orders.');
      setTenantDrill(null);
    } finally {
      setTenantDrillLoading(false);
    }
  };

  const load = useCallback(async (section = tab) => {
    setLoading(true);
    try {
      if (section === 'dashboard' || section === 'all') {
        const r = await apiClient.get(`/ultra-admin/sales/dashboard`);
        setDashboard(r.data);
      }
      if (section === 'by-tenant' || section === 'all') {
        const r = await apiClient.get(`/ultra-admin/sales/by-tenant`);
        setByTenant(r.data.items || []);
      }
      if (section === 'by-zone' || section === 'all') {
        const r = await apiClient.get(`/ultra-admin/sales/by-zone`);
        setByZone(r.data.items || []);
      }
      if (section === 'abandoned' || section === 'all') {
        const r = await apiClient.get(`/ultra-admin/sales/abandoned-carts`);
        setAbandoned(r.data.items || []);
      }
    } catch (e) {
      if (e?.response?.status === 401) navigate('/giris');
      else toast.error(tr ? 'Veriler yüklenemedi.' : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [tab, navigate, tr]);

  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line

  const sendReminder = async (cart) => {
    setSendingTo(cart.user_id);
    try {
      const hoursLeft = cart.earliest_expiry
        ? Math.max(0, (new Date(cart.earliest_expiry) - new Date()) / 3600000)
        : null;
      const r = await apiClient.post(`/ultra-admin/sales/abandoned-carts/send-reminder`, {
        user_email:   cart.user_email,
        company_name: cart.company_name,
        cart_value:   cart.cart_value,
        items:        cart.items,
        hours_left:   hoursLeft,
      });
      if (r.data.success) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch { toast.error(tr ? 'Mail gönderilemedi.' : 'Failed to send email.'); }
    finally { setSendingTo(null); }
  };

  // ── Abandoned cart destructive actions ──────────────────────────────────────
  const deleteWholeCart = async (cart) => {
    if (!cart?.user_id) return;
    setDeleting(cart.user_id);
    try {
      const r = await apiClient.delete(
        `/ultra-admin/sales/abandoned-carts/${encodeURIComponent(cart.user_id)}`
      );
      if (r.data?.success) {
        toast.success(tr
          ? `Sepet silindi (${r.data.items_deleted || 0} ürün).`
          : `Cart deleted (${r.data.items_deleted || 0} items).`);
        setAbandoned(prev => prev.filter(c => c.user_id !== cart.user_id));
        if (expandedCart === cart.user_id) setExpandedCart(null);
      } else {
        toast.error(tr ? 'Silinemedi.' : 'Failed to delete.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || (tr ? 'Silinemedi.' : 'Failed to delete.');
      toast.error(msg);
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  };

  const deleteCartItem = async (cart, item) => {
    // item matching must align with backend: id | line_id | product_id
    const itemId = item?.id || item?.line_id || item?.product_id;
    if (!cart?.user_id || !itemId) {
      toast.error(tr ? 'Ürün tanımlayıcısı yok.' : 'Missing item identifier.');
      return;
    }
    setDeletingItem(`${cart.user_id}:${itemId}`);
    try {
      const r = await apiClient.delete(
        `/ultra-admin/sales/abandoned-carts/${encodeURIComponent(cart.user_id)}/items/${encodeURIComponent(itemId)}`
      );
      if (r.data?.success) {
        toast.success(tr
          ? `Ürün sepetten çıkarıldı (kalan: ${r.data.remaining_items}).`
          : `Item removed (remaining: ${r.data.remaining_items}).`);
        if (r.data.remaining_items === 0) {
          // Backend drops the cart entirely
          setAbandoned(prev => prev.filter(c => c.user_id !== cart.user_id));
          if (expandedCart === cart.user_id) setExpandedCart(null);
        } else {
          // Refresh the single affected cart (easiest: reload)
          await load('abandoned');
        }
      } else {
        toast.error(tr ? 'Silinemedi.' : 'Failed to delete.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || (tr ? 'Silinemedi.' : 'Failed to delete.');
      toast.error(msg);
    } finally {
      setDeletingItem(null);
      setConfirmDelete(null);
    }
  };

  // Delete an order (orders collection). Unpaid / pending orders delete
  // immediately; paid / confirmed ones require Ultra Password confirmation
  // (backend 409 → we surface a password prompt and retry with ?ultra_password=).
  const deleteOrder = async (order, ultraPassword = '') => {
    if (!order?.id) return;
    setDeletingOrder(order.id);
    try {
      const url = `/ultra-admin/sales/orders/${encodeURIComponent(order.id)}`
        + (ultraPassword ? `?ultra_password=${encodeURIComponent(ultraPassword)}` : '');
      const r = await apiClient.delete(url);
      if (r.data?.success) {
        toast.success(tr
          ? `Sipariş silindi (${order.order_number || order.id}).`
          : `Order deleted (${order.order_number || order.id}).`);
        // Update the tenant drill-down modal: remove the order locally
        setTenantDrill(prev => prev
          ? { ...prev, orders: (prev.orders || []).filter(x => x.id !== order.id) }
          : prev);
        if (expandedOrderId === order.id) setExpandedOrderId(null);
        // Refresh the By-Tenant totals next time user reopens / triggers reload
        load('by-tenant');
        setConfirmDelete(null);
        setForceDeleteOrder(null);
      } else {
        toast.error(tr ? 'Silinemedi.' : 'Failed to delete.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend signalled "paid / fulfilled — needs Ultra Password". Swap the
      // confirm dialog for a password-entry one.
      const code = typeof detail === 'object' ? detail?.code : null;
      if (e?.response?.status === 409 && code === 'ULTRA_PASSWORD_REQUIRED') {
        setConfirmDelete(null);
        setForceDeleteOrder({ order, payment_status: detail.payment_status, status: detail.status });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || (tr ? 'Silinemedi.' : 'Failed to delete.'));
        toast.error(msg);
      }
    } finally {
      setDeletingOrder(null);
    }
  };

  // 2-decimal consistent money formatting, locale-aware (tr-TR: 40.000,00 · en-US: 40,000.00)
  const locale = tr ? 'tr-TR' : 'en-US';
  const fmt = (n, decimals = 0) => Number(n || 0).toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const fmtMoney = (n, cur = 'USD') => `${cur} ${fmt(n, 2)}`;

  const tabs = [
    { key: 'dashboard', label: tr ? 'Satış Dashboard' : 'Sales Dashboard', icon: BarChart2 },
    { key: 'by-tenant', label: tr ? "Tenant'a Göre" : 'By Tenant',          icon: Globe },
    { key: 'by-zone',   label: tr ? "Zone'a Göre"   : 'By Zone',            icon: PieChart },
    { key: 'abandoned', label: tr ? 'Sepet Terk'    : 'Abandoned Carts',    icon: ShoppingCart },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 overflow-auto">
        <UltraAdminTopbar
          title={tr ? 'Alışveriş Raporları' : 'Sales Reports'}
          subtitle={tr ? 'Gerçek zamanlı satış ve sepet analitiği' : 'Real-time sales & cart analytics'}
          icon={TrendingUp}
          onRefresh={() => load(tab)}
        />
        <div className="px-6 pt-5">
          <PageIntro {...getPageIntro('sales', language)} />
        </div>
        {/* Sub-tabs */}
        <div className="bg-white border-b border-gray-100 px-6">
          <div className="flex gap-0.5">
            {tabs.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`sales-tab-${t.key}`}
                  onClick={() => changeTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-all ${
                    active
                      ? 'border-emerald-600 text-emerald-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  {t.key === 'abandoned' && abandoned.length > 0 && (
                    <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {abandoned.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {loading && !dashboard && !byTenant.length && !byZone.length && !abandoned.length ? (
            <div className="flex justify-center py-24">
              <Loader2 className="h-10 w-10 text-emerald-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* ── DASHBOARD TAB ── */}
              {tab === 'dashboard' && dashboard && (
                <div className="space-y-6">
                  {/* KPI Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={DollarSign} label={tr ? 'Toplam Gelir' : 'Total Revenue'}
                      value={fmtMoney(dashboard.total_revenue)}
                      sub={tr ? `Bu ay: ${fmtMoney(dashboard.month_revenue)}` : `This month: ${fmtMoney(dashboard.month_revenue)}`}
                      color="emerald" />
                    <StatCard icon={Package} label={tr ? 'Toplam Sipariş' : 'Total Orders'}
                      value={fmt(dashboard.total_orders)}
                      sub={tr ? `Bu hafta: ${dashboard.week_orders}` : `This week: ${dashboard.week_orders}`}
                      color="blue" />
                    <StatCard icon={CheckCircle} label={tr ? 'Tamamlanan' : 'Completed'}
                      value={fmt(dashboard.completed_orders)}
                      sub={tr ? `Ort. sipariş: ${fmtMoney(dashboard.avg_order_value)}` : `Avg order: ${fmtMoney(dashboard.avg_order_value)}`}
                      color="purple" />
                    <StatCard icon={AlertTriangle} label={tr ? 'Bekleyen' : 'Pending'}
                      value={fmt(dashboard.pending_orders)}
                      sub={tr ? 'Onay bekliyor' : 'Awaiting confirmation'}
                      color="amber" />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Revenue Chart */}
                    <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-800">{tr ? 'Son 7 Gün Geliri' : 'Last 7 Days Revenue'}</h3>
                          <p className="text-xs text-gray-400">{tr ? 'Günlük satış trendi' : 'Daily revenue trend'}</p>
                        </div>
                        <span className="text-sm font-bold text-emerald-600">{fmtMoney(dashboard.week_revenue)}</span>
                      </div>
                      <MiniBarChart data={dashboard.daily_chart} valueKey="revenue" labelKey="day" />
                    </div>

                    {/* Conversion + Cart */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <p className="text-xs font-semibold text-gray-400 uppercase">{tr ? 'Dönüşüm Oranı' : 'Conversion Rate'}</p>
                        <p className="text-3xl font-extrabold text-gray-900 mt-1">%{dashboard.conversion_rate}</p>
                        <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all"
                            style={{ width: `${dashboard.conversion_rate}%` }} />
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          {dashboard.completed_orders}/{dashboard.total_orders} {tr ? 'sipariş tamamlandı' : 'orders completed'}
                        </p>
                      </div>
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                        <p className="text-xs font-semibold text-gray-400 uppercase">{tr ? 'Aktif Sepetler' : 'Active Carts'}</p>
                        <p className="text-3xl font-extrabold text-gray-900 mt-1">{dashboard.active_carts}</p>
                        <p className="text-xs text-amber-600 mt-1 font-medium">
                          {fmtMoney(dashboard.cart_total_value)} {tr ? 'bekliyor' : 'pending checkout'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Today highlight */}
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-5 text-white flex items-center justify-between">
                    <div>
                      <p className="text-emerald-200 text-sm font-semibold uppercase">{tr ? 'Bugün' : 'Today'}</p>
                      <p className="text-3xl font-extrabold mt-1">{fmtMoney(dashboard.today_revenue)}</p>
                      <p className="text-emerald-200 text-sm mt-0.5">{dashboard.today_orders} {tr ? 'sipariş' : 'orders'}</p>
                    </div>
                    <TrendingUp className="h-16 w-16 text-emerald-400 opacity-40" />
                  </div>
                </div>
              )}

              {/* ── BY TENANT TAB ── */}
              {tab === 'by-tenant' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-5 border-b border-gray-50 flex items-center justify-between">
                    <div>
                      <h2 className="font-bold text-gray-900">{tr ? "Tenant'a Göre Satışlar" : 'Sales by Tenant'}</h2>
                      <p className="text-xs text-gray-400">{byTenant.length} {tr ? 'tenant' : 'tenants'}</p>
                    </div>
                  </div>
                  {byTenant.length === 0 ? (
                    <div className="py-16 text-center text-gray-400">{tr ? 'Henüz sipariş yok.' : 'No orders yet.'}</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {[tr ? 'Şirket' : 'Company', 'Zone', tr ? 'Siparişler' : 'Orders',
                            tr ? 'Gelir' : 'Revenue', tr ? 'Tamamlanan' : 'Completed', tr ? 'Bekleyen' : 'Pending'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {byTenant.map((t, i) => (
                          <tr
                            key={t.tenant_id || i}
                            onClick={() => openTenantDrill(t.tenant_id)}
                            className="hover:bg-emerald-50/60 transition-colors cursor-pointer"
                            data-testid={`tenant-row-${t.tenant_id}`}
                          >
                            <td className="px-4 py-3.5 font-semibold text-gray-800 flex items-center gap-2">
                              {t.company_name}
                              <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
                            </td>
                            <td className="px-4 py-3.5"><ZoneBadge zone={t.zone} /></td>
                            <td className="px-4 py-3.5 text-gray-600">{t.orders}</td>
                            <td className="px-4 py-3.5 font-bold text-emerald-700">{fmtMoney(t.revenue)}</td>
                            <td className="px-4 py-3.5">
                              <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full text-xs font-semibold">{t.completed}</span>
                            </td>
                            <td className="px-4 py-3.5">
                              {t.pending > 0
                                ? <span className="text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full text-xs font-semibold">{t.pending}</span>
                                : <span className="text-gray-300">—</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ── BY ZONE TAB ── */}
              {tab === 'by-zone' && (
                <div className="space-y-4">
                  {byZone.length === 0 ? (
                    <div className="bg-white rounded-2xl p-16 text-center text-gray-400 shadow-sm border">{tr ? 'Henüz sipariş yok.' : 'No orders yet.'}</div>
                  ) : (
                    <>
                      {/* Zone cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {byZone.map((z, i) => {
                          const totalRev = byZone.reduce((s, x) => s + x.revenue, 0);
                          const pct = totalRev > 0 ? Math.round(z.revenue / totalRev * 100) : 0;
                          const gradients = ['from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600', 'from-orange-500 to-amber-600'];
                          return (
                            <div key={z.zone || i} className={`bg-gradient-to-br ${gradients[i % 3]} rounded-2xl p-5 text-white shadow-md`}>
                              <p className="text-sm font-semibold opacity-80">{z.zone}</p>
                              <p className="text-3xl font-extrabold mt-1">{fmtMoney(z.revenue)}</p>
                              <div className="flex justify-between items-end mt-4">
                                <div className="text-xs opacity-75">
                                  <p>{z.orders} {tr ? 'sipariş' : 'orders'}</p>
                                  <p>{z.tenants} {tr ? 'tenant' : 'tenants'}</p>
                                </div>
                                <span className="text-4xl font-black opacity-20">%{pct}</span>
                              </div>
                              <div className="mt-3 h-1.5 bg-white/20 rounded-full">
                                <div className="h-full bg-white/70 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Zone table */}
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              {['Zone', tr ? 'Tenant Sayısı' : 'Tenants', tr ? 'Sipariş' : 'Orders',
                                tr ? 'Gelir' : 'Revenue', tr ? 'Pay' : 'Share'].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {byZone.map((z, i) => {
                              const totalRev = byZone.reduce((s, x) => s + x.revenue, 0);
                              const pct = totalRev > 0 ? Math.round(z.revenue / totalRev * 100) : 0;
                              return (
                                <tr key={z.zone || i} className="hover:bg-gray-50">
                                  <td className="px-4 py-3.5"><ZoneBadge zone={z.zone} /></td>
                                  <td className="px-4 py-3.5 text-gray-600">{z.tenants}</td>
                                  <td className="px-4 py-3.5 text-gray-600">{z.orders}</td>
                                  <td className="px-4 py-3.5 font-bold text-emerald-700">{fmtMoney(z.revenue)}</td>
                                  <td className="px-4 py-3.5">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-2 bg-gray-100 rounded-full max-w-[80px]">
                                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs text-gray-500">%{pct}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── ABANDONED CARTS TAB ── */}
              {tab === 'abandoned' && (
                <div className="space-y-4">
                  {/* Summary bar */}
                  {abandoned.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3.5 flex items-center gap-4">
                      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-800">
                          {abandoned.length} {tr ? 'aktif sepet bekliyor' : 'active carts pending checkout'}
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          {tr ? 'Toplam bekleme değeri:' : 'Total pending value:'}{' '}
                          <strong>USD {fmt(abandoned.reduce((s, c) => s + c.cart_value, 0))}</strong>
                        </p>
                      </div>
                    </div>
                  )}

                  {abandoned.length === 0 ? (
                    <div className="bg-white rounded-2xl p-16 text-center shadow-sm border">
                      <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-400 font-medium">{tr ? 'Aktif sepet yok.' : 'No active carts.'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {abandoned.map((cart, idx) => {
                        const isExpanded = expandedCart === cart.user_id;
                        const urgentItems = cart.items.filter(i => {
                          if (!i.expires_at) return false;
                          const diff = new Date(i.expires_at) - new Date();
                          return diff > 0 && diff < 3 * 3600000;
                        });
                        return (
                          <div key={cart.user_id || idx}
                            data-testid={`abandoned-cart-${idx}`}
                            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:border-amber-200 transition-all">
                            <div className="p-4 flex items-center gap-4">
                              {/* Avatar */}
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                {(cart.company_name || cart.user_email || '?')[0].toUpperCase()}
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-gray-800 truncate">{cart.company_name || cart.user_email}</p>
                                  <ZoneBadge zone={cart.zone} />
                                  {urgentItems.length > 0 && (
                                    <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full animate-pulse">
                                      {tr ? 'ACİL' : 'URGENT'}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">{cart.user_email}</p>
                                <div className="flex items-center gap-3 mt-1.5">
                                  <span className="text-xs text-gray-500">
                                    {cart.items_count} {tr ? 'ürün' : 'items'}
                                  </span>
                                  <span className="text-[10px] text-gray-300">·</span>
                                  <span className="text-xs font-bold text-emerald-700">USD {fmt(cart.cart_value)}</span>
                                  {cart.hours_in_cart != null && (
                                    <>
                                      <span className="text-[10px] text-gray-300">·</span>
                                      <span className={`flex items-center gap-1 text-xs ${cart.hours_in_cart > 12 ? 'text-red-500' : 'text-amber-500'}`}>
                                        <Clock className="h-3 w-3" />
                                        {cart.hours_in_cart.toFixed(1)} {tr ? 'saattir bekliyor' : 'hrs in cart'}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Earliest expiry countdown */}
                              <div className="text-right shrink-0 space-y-1">
                                {cart.earliest_expiry && (
                                  <div className="flex flex-col items-end">
                                    <span className="text-[10px] text-gray-400">{tr ? 'En yakın süre sonu:' : 'Earliest expiry:'}</span>
                                    <Countdown expiresAt={cart.earliest_expiry} />
                                  </div>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  onClick={() => setExpandedCart(isExpanded ? null : cart.user_id)}
                                  className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500 transition-colors"
                                  title={tr ? 'Detay' : 'Details'}
                                >
                                  <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>
                                <button
                                  data-testid={`send-reminder-${idx}`}
                                  onClick={() => sendReminder(cart)}
                                  disabled={sendingTo === cart.user_id}
                                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
                                >
                                  {sendingTo === cart.user_id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Send className="h-3.5 w-3.5" />
                                  }
                                  {tr ? 'Mail Gönder' : 'Send Reminder'}
                                </button>
                                <button
                                  data-testid={`delete-cart-${idx}`}
                                  onClick={() => setConfirmDelete({ scope: 'cart', cart })}
                                  disabled={deleting === cart.user_id}
                                  className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 disabled:opacity-50 text-red-600 border border-red-200 text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
                                  title={tr ? 'Sepeti tamamen sil' : 'Delete entire cart'}
                                >
                                  {deleting === cart.user_id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Trash2 className="h-3.5 w-3.5" />
                                  }
                                  {tr ? 'Sepeti Sil' : 'Delete Cart'}
                                </button>
                              </div>
                            </div>

                            {/* Expanded items */}
                            {isExpanded && (
                              <div className="border-t border-gray-50 bg-gray-50 px-4 py-3">
                                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">{tr ? 'Sepet İçeriği' : 'Cart Contents'}</p>
                                <div className="space-y-1.5">
                                  {cart.items.map((item, ii) => {
                                    const itemKey = item.id || item.line_id || item.product_id;
                                    const busy = deletingItem === `${cart.user_id}:${itemKey}`;
                                    return (
                                      <div key={ii} className="flex items-center gap-3 text-sm">
                                        <span className="flex-1 text-gray-700 font-medium">{item.product_name}</span>
                                        <span className="text-xs text-gray-400">{item.quantity}x</span>
                                        <span className="font-semibold text-emerald-700">{item.currency} {fmt(item.line_total)}</span>
                                        {item.expires_at && (
                                          <div className="flex items-center gap-1">
                                            <Clock className="h-3 w-3 text-gray-300" />
                                            <Countdown expiresAt={item.expires_at} />
                                          </div>
                                        )}
                                        <button
                                          data-testid={`delete-cart-item-${idx}-${ii}`}
                                          onClick={() => setConfirmDelete({ scope: 'item', cart, item })}
                                          disabled={busy || !itemKey}
                                          className="p-1.5 rounded-lg border border-red-100 text-red-500 hover:bg-red-50 disabled:opacity-40 transition-colors"
                                          title={itemKey ? (tr ? 'Ürünü sepetten çıkar' : 'Remove item from cart') : (tr ? 'Tanımlayıcı yok' : 'No identifier')}
                                        >
                                          {busy
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            : <Trash2 className="h-3.5 w-3.5" />
                                          }
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Tenant Drill-down Modal ────────────────────────────────────────── */}
      {tenantDrill && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setTenantDrill(null)}
          data-testid="tenant-drill-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600">
              <div className="text-white">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  {tenantDrill.company_name || (tr ? 'Tenant' : 'Tenant')}
                </h2>
                <p className="text-xs opacity-80 mt-0.5">
                  <ZoneBadge zone={tenantDrill.zone} /> · {tenantDrill.orders?.length || 0} {tr ? 'sipariş' : 'orders'}
                </p>
              </div>
              <button onClick={() => setTenantDrill(null)} className="text-white/80 hover:text-white p-1 rounded-full"
                data-testid="tenant-drill-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 overflow-y-auto flex-1 space-y-2">
              {tenantDrillLoading ? (
                <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tr ? 'Yükleniyor...' : 'Loading...'}
                </div>
              ) : !tenantDrill.orders || tenantDrill.orders.length === 0 ? (
                <div className="py-12 text-center text-gray-400">{tr ? 'Henüz sipariş yok.' : 'No orders yet.'}</div>
              ) : (
                tenantDrill.orders.map((o) => {
                  const isExp = expandedOrderId === o.id;
                  const status = o.status || '—';
                  const STATUS_LABELS = {
                    pending_confirmation: tr ? 'Onay Bekliyor'    : 'Awaiting Confirmation',
                    acknowledged:         tr ? 'Kabul Edildi'     : 'Acknowledged',
                    in_progress:          tr ? 'Hazırlanıyor'     : 'In Progress',
                    shipped:              tr ? 'Kargoda'          : 'Shipped',
                    delivered:            tr ? 'Teslim Edildi'    : 'Delivered',
                    confirmed:            tr ? 'Onaylandı'        : 'Confirmed',
                    completed:            tr ? 'Tamamlandı'       : 'Completed',
                    paid:                 tr ? 'Ödendi'           : 'Paid',
                    cancelled:            tr ? 'İptal Edildi'     : 'Cancelled',
                    rejected:             tr ? 'Reddedildi'       : 'Rejected',
                    quote_pending:        tr ? 'Teklif Bekliyor'  : 'Quote Pending',
                    quoted:               tr ? 'Teklif Sunuldu'   : 'Quoted',
                    pending:              tr ? 'Beklemede'        : 'Pending',
                    draft:                tr ? 'Taslak'           : 'Draft',
                  };
                  const statusLabel = STATUS_LABELS[status] || status;
                  const statusClr =
                    status === 'confirmed' || status === 'paid' || status === 'completed'
                      ? 'bg-emerald-100 text-emerald-700'
                      : status === 'pending' || status === 'quote_pending' || status === 'acknowledged'
                      ? 'bg-amber-100 text-amber-700'
                      : status === 'cancelled' || status === 'rejected'
                      ? 'bg-red-100 text-red-600'
                      : 'bg-gray-100 text-gray-600';
                  return (
                    <div
                      key={o.id}
                      className="rounded-2xl border border-gray-100 bg-white hover:bg-gray-50 transition-colors"
                      data-testid={`order-row-${o.id}`}
                    >
                      {/* Summary */}
                      <div className="flex items-center gap-2 pr-3">
                        <button
                          type="button"
                          onClick={() => setExpandedOrderId(isExp ? null : o.id)}
                          className="flex-1 flex items-center justify-between gap-3 px-4 py-3 text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${isExp ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500'} shrink-0`}>
                              <ShoppingCart className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 text-sm truncate">
                                {o.order_title || o.order_number}
                                <span className="ml-2 text-[11px] font-normal text-gray-400">{o.order_number}</span>
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {o.created_at ? new Date(o.created_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : '—'}
                                {o.order_type && <> · <span className="font-medium">{o.order_type}</span></>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-sm font-bold text-emerald-700">{fmtMoney(o.total_amount, o.currency)}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusClr}`}>{statusLabel}</span>
                            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                          </div>
                        </button>
                        {isOrderDeletable(o) && (
                          <button
                            type="button"
                            data-testid={`delete-order-${o.id}`}
                            onClick={(e) => { e.stopPropagation(); setConfirmDelete({ scope: 'order', order: o }); }}
                            disabled={deletingOrder === o.id}
                            className="p-2 rounded-xl border border-red-100 text-red-500 hover:bg-red-50 disabled:opacity-40 transition-colors shrink-0"
                            title={tr ? 'Ödenmemiş siparişi sil' : 'Delete unpaid order'}
                          >
                            {deletingOrder === o.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />
                            }
                          </button>
                        )}
                      </div>

                      {/* Expanded details: item list */}
                      {isExp && (
                        <div className="border-t border-gray-50 bg-gray-50/70 px-4 py-3 space-y-2">
                          {(o.items || []).length === 0 ? (
                            <p className="text-xs text-gray-400 py-2">{tr ? 'Bu siparişte ürün satırı yok.' : 'No line items.'}</p>
                          ) : (
                            (o.items || []).map((it, idx) => {
                              const isFlat = (it.calculation_type || '') === 'flat';
                              const mode = it.pricing_mode_selected || '';
                              const MODE_LABEL = {
                                daily: tr ? 'Günlük'   : 'Daily',
                                event: tr ? 'Etkinlik' : 'Event',
                              };
                              return (
                              <div key={idx} className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-800 truncate">
                                    {it.product_name || it.product_title || (tr ? 'Ürün' : 'Item')}
                                  </p>
                                  <p className="text-[11px] text-gray-500 mt-0.5">
                                    {/* Simple summary: qty × unit, then duration/mode chips without implying multiplication */}
                                    {it.quantity || 1} × {fmtMoney(it.unit_price, o.currency)}
                                    {(it.number_of_days && !isFlat) && <> · {it.number_of_days} {tr ? 'gün' : 'days'}</>}
                                    {mode && <> · <span className="uppercase text-gray-400">{MODE_LABEL[mode] || mode}</span></>}
                                  </p>
                                  {(it.start_date || it.end_date) && (
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                      {it.start_date}{it.end_date && it.end_date !== it.start_date ? ` → ${it.end_date}` : ''}
                                      {isFlat && it.number_of_days && <> · {it.number_of_days} {tr ? 'gün' : 'days'}</>}
                                    </p>
                                  )}
                                  {(it.is_customized || it.customization_notes) && (
                                    <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50/70 px-2 py-1.5">
                                      <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-0.5">
                                        {tr ? 'Özel Talep' : 'Custom Request'}
                                      </p>
                                      {it.customization_notes && (
                                        <div
                                          className="text-[11px] text-amber-900/80 prose prose-sm max-w-none"
                                          dangerouslySetInnerHTML={{ __html: it.customization_notes }}
                                        />
                                      )}
                                    </div>
                                  )}
                                </div>
                                <span className="text-sm font-bold text-emerald-700 shrink-0">{fmtMoney(it.line_total, o.currency)}</span>
                              </div>
                              );
                            })
                          )}
                          {o.notes && (
                            <p className="text-[11px] italic text-gray-500 pt-2 border-t border-gray-100">
                              <span className="font-semibold">{tr ? 'Not: ' : 'Note: '}</span>
                              {o.notes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer summary */}
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-sm">
              <span className="text-gray-600">{tr ? 'Toplam' : 'Total'}: {tenantDrill.orders?.length || 0} {tr ? 'sipariş' : 'orders'}</span>
              <span className="font-bold text-emerald-700">
                {fmtMoney((tenantDrill.orders || []).reduce((s, o) => s + (parseFloat(o.total_amount) || 0), 0))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="confirm-delete-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 bg-red-50">
              <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-red-800">
                  {confirmDelete.scope === 'cart'
                    ? (tr ? 'Sepeti tamamen sil' : 'Delete entire cart')
                    : confirmDelete.scope === 'item'
                    ? (tr ? 'Ürünü sepetten çıkar' : 'Remove item from cart')
                    : (tr ? 'Ödenmemiş siparişi sil' : 'Delete unpaid order')}
                </h3>
                <p className="text-[11px] text-red-600 mt-0.5">
                  {tr ? 'Bu işlem geri alınamaz.' : 'This action cannot be undone.'}
                </p>
              </div>
            </div>

            <div className="px-5 py-4 text-sm text-gray-700 space-y-2">
              {confirmDelete.scope === 'order' ? (
                <>
                  <p className="text-xs text-gray-500">
                    {tr ? 'Sipariş No' : 'Order #'}:
                    <span className="font-semibold text-gray-800 ml-1">
                      {confirmDelete.order?.order_number || confirmDelete.order?.id}
                    </span>
                  </p>
                  <p>
                    {tr
                      ? <>Durum: <strong>{confirmDelete.order?.status}</strong></>
                      : <>Status: <strong>{confirmDelete.order?.status}</strong></>}
                    {' · '}
                    <strong>{(confirmDelete.order?.currency || 'USD')} {fmt(confirmDelete.order?.total_amount, 2)}</strong>
                  </p>
                  <p className="text-[12px] text-gray-500">
                    {tr
                      ? 'Sipariş kalıcı olarak silinecek. Ödenmiş / onaylanmış siparişlerde ek olarak Ultra Password istenir.'
                      : 'The order will be permanently deleted. Paid / confirmed orders additionally require your Ultra Password.'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500">
                    {tr ? 'Kullanıcı' : 'User'}:
                    <span className="font-semibold text-gray-800 ml-1">
                      {confirmDelete.cart?.company_name || confirmDelete.cart?.user_email}
                    </span>
                    <span className="text-gray-400 ml-1">· {confirmDelete.cart?.user_email}</span>
                  </p>
                  {confirmDelete.scope === 'cart' ? (
                    <p>
                      {tr
                        ? <>Sepetteki <strong>{confirmDelete.cart?.items_count}</strong> ürün (<strong>USD {fmt(confirmDelete.cart?.cart_value)}</strong>) kalıcı olarak silinecek. Ödenmiş siparişler <em>etkilenmez</em>.</>
                        : <>The <strong>{confirmDelete.cart?.items_count}</strong> items (<strong>USD {fmt(confirmDelete.cart?.cart_value)}</strong>) will be permanently removed. Paid orders are <em>not affected</em>.</>}
                    </p>
                  ) : (
                    <p>
                      {tr
                        ? <>Bu ürün sepetten çıkarılacak:</>
                        : <>This item will be removed from the cart:</>}
                      <br />
                      <strong className="text-gray-900">{confirmDelete.item?.product_name}</strong>{' '}
                      <span className="text-xs text-gray-500">
                        ({confirmDelete.item?.quantity}× · {confirmDelete.item?.currency} {fmt(confirmDelete.item?.line_total)})
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
              <button
                data-testid="confirm-delete-cancel"
                onClick={() => setConfirmDelete(null)}
                className="text-sm text-gray-600 hover:text-gray-800 px-4 py-2 rounded-xl border border-gray-200 bg-white"
              >
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="confirm-delete-confirm"
                onClick={() => {
                  if (confirmDelete.scope === 'cart')  deleteWholeCart(confirmDelete.cart);
                  else if (confirmDelete.scope === 'item') deleteCartItem(confirmDelete.cart, confirmDelete.item);
                  else if (confirmDelete.scope === 'order') deleteOrder(confirmDelete.order);
                }}
                disabled={
                  (confirmDelete.scope === 'cart'  && deleting === confirmDelete.cart?.user_id) ||
                  (confirmDelete.scope === 'item'  && deletingItem?.startsWith(`${confirmDelete.cart?.user_id}:`)) ||
                  (confirmDelete.scope === 'order' && deletingOrder === confirmDelete.order?.id)
                }
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl"
              >
                {((confirmDelete.scope === 'cart'  && deleting === confirmDelete.cart?.user_id) ||
                  (confirmDelete.scope === 'item'  && deletingItem?.startsWith(`${confirmDelete.cart?.user_id}:`)) ||
                  (confirmDelete.scope === 'order' && deletingOrder === confirmDelete.order?.id))
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Trash2 className="h-4 w-4" />
                }
                {confirmDelete.scope === 'cart'
                  ? (tr ? 'Sepeti Sil' : 'Delete Cart')
                  : confirmDelete.scope === 'item'
                  ? (tr ? 'Ürünü Çıkar' : 'Remove Item')
                  : (tr ? 'Siparişi Sil' : 'Delete Order')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Force-delete modal (paid / confirmed orders) ──────────────────── */}
      {forceDeleteOrder && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { setForceDeleteOrder(null); setForcePassword(''); }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden ring-2 ring-red-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-red-50 border-b border-red-200 flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-red-900">
                  {tr ? 'Ödenmiş Siparişi Kalıcı Sil' : 'Hard-Delete Paid Order'}
                </h3>
                <p className="text-xs text-red-700 mt-1 leading-relaxed">
                  {tr
                    ? `Bu sipariş "${forceDeleteOrder.status}" durumunda${forceDeleteOrder.payment_status === 'paid' ? ' ve ödenmiş' : ''}. Geri alınamaz — devam etmek için Ultra Password gerekli.`
                    : `This order is "${forceDeleteOrder.status}"${forceDeleteOrder.payment_status === 'paid' ? ' and paid' : ''}. Irreversible — enter your Ultra Password to proceed.`}
                </p>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="text-xs text-gray-600 space-y-1">
                <div>
                  <span className="text-gray-400">{tr ? 'Sipariş No:' : 'Order #'}</span>
                  <span className="font-semibold text-gray-800 ml-1">
                    {forceDeleteOrder.order?.order_number || forceDeleteOrder.order?.id}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">{tr ? 'Toplam:' : 'Total:'}</span>
                  <span className="font-semibold text-gray-800 ml-1">
                    {forceDeleteOrder.order?.currency || 'USD'} {fmt(forceDeleteOrder.order?.total_amount, 2)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  {tr ? 'Ultra Password' : 'Ultra Password'}
                </label>
                <input
                  data-testid="force-delete-password"
                  type="password"
                  autoFocus
                  value={forcePassword}
                  onChange={(e) => setForcePassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && forcePassword.trim()) {
                      deleteOrder(forceDeleteOrder.order, forcePassword.trim());
                    }
                  }}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder={tr ? 'Ultra Password girin' : 'Enter Ultra Password'}
                />
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
              <button
                data-testid="force-delete-cancel"
                onClick={() => { setForceDeleteOrder(null); setForcePassword(''); }}
                className="text-sm text-gray-600 hover:text-gray-800 px-4 py-2 rounded-xl border border-gray-200 bg-white"
              >
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="force-delete-confirm"
                onClick={() => {
                  if (!forcePassword.trim()) return;
                  deleteOrder(forceDeleteOrder.order, forcePassword.trim());
                }}
                disabled={!forcePassword.trim() || deletingOrder === forceDeleteOrder.order?.id}
                className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl"
              >
                {deletingOrder === forceDeleteOrder.order?.id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Trash2 className="h-4 w-4" />}
                {tr ? 'Kalıcı Sil' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
